import {
  ARENA_H,
  ARENA_W,
  COLOR,
  DRAGON,
  ENEMY,
  HEART,
  JUICE,
  PARTICLE_BUDGET,
  PLAYER,
  POOL,
  SWORD,
} from "./config.ts";
import { Pool } from "./core/pool.ts";
import { RNG } from "./core/rng.ts";
import { Signal } from "./core/signal.ts";
import { circlesOverlap, clamp, damp, dist2, TAU } from "./core/vec.ts";
import { Dragon } from "./entities/dragon.ts";
import { Enemy, type EnemyType } from "./entities/enemy.ts";
import { HZ_CIRCLE, Hazard } from "./entities/hazard.ts";
import { Heart } from "./entities/heart.ts";
import { Particle } from "./entities/particle.ts";
import { Player } from "./entities/player.ts";
import { hazardHitsCircle } from "./systems/collision.ts";
import type { DragonCmd, EnemyAttack, SpawnContext } from "./content/levelTypes.ts";

export interface HitEvent {
  x: number;
  y: number;
  fatal: boolean;
}

/** Per-frame movement intent fed in by the game layer (from Input). */
export interface MoveIntent {
  pointerActive: boolean;
  targetX: number;
  targetY: number;
  axisX: number;
  axisY: number;
}

/**
 * The World owns all simulation state (player, pooled hazards & particles) and
 * advances it in deterministic fixed steps. It implements SpawnContext so
 * patterns can emit hazards into it. Rendering and audio subscribe to its
 * signals; it knows nothing about Pixi or the DOM.
 */
export class World implements SpawnContext {
  readonly rng = new RNG();
  readonly arenaW = ARENA_W;
  readonly arenaH = ARENA_H;
  readonly player = new Player();

  readonly hazards = new Pool<Hazard>(POOL.bullets, () => new Hazard());
  readonly enemies = new Pool<Enemy>(POOL.enemies, () => new Enemy());
  readonly particles = new Pool<Particle>(POOL.particles, () => new Particle());
  readonly hearts = new Pool<Heart>(POOL.hearts, () => new Heart());
  readonly boss = new Dragon();

  readonly onHit = new Signal<HitEvent>();
  readonly onGraze = new Signal<{ x: number; y: number }>();
  readonly onHeal = new Signal<{ x: number; y: number }>();
  readonly onSpawn = new Signal<void>();
  readonly onEnemySpawn = new Signal<Enemy>();
  readonly onEnemyDeath = new Signal<Enemy>();
  /** A sword swing happened; `hits` = enemies struck, `cut` = bullets destroyed. */
  readonly onSlash = new Signal<{ hits: number; cut: number }>();
  /** A bomb (expire-burst hazard) detonated. */
  readonly onExplosion = new Signal<{ x: number; y: number }>();
  readonly onBoss = new Signal<DragonCmd>();
  readonly onRoar = new Signal<void>();
  /** The dragon's HP hit zero — felled early by the sword. */
  readonly onBossDefeated = new Signal<void>();
  /** An enemy launched an attack (archer dart, bomb hurl, beast charge, …). */
  readonly onEnemyAttack = new Signal<EnemyAttack>();

  /** Realtime hit-stop timer; while > 0 the loop runs the sim in slow-mo. */
  hitStop = 0;
  /** Decaying screen-shake magnitude (consumed by the renderer). */
  shake = 0;
  /** Countdown to the next heart-pickup spawn attempt. */
  private heartTimer = 0;
  /** Per-level hazard-speed multiplier (1 = base). Set by the game on load. */
  difficulty = 1;
  /** True while the level is winding down; the player can't be hit. */
  outro = false;
  /** Full-screen damage flash [0..1] (consumed by the renderer). */
  flash = 0;
  /** Sword state: cooldown + visual sweep timer (renderer reads slashFx). */
  slashCd = 0;
  slashFx = 0;

  private intent: MoveIntent = {
    pointerActive: false,
    targetX: ARENA_W / 2,
    targetY: ARENA_H * 0.78,
    axisX: 0,
    axisY: 0,
  };

  get playerX(): number {
    return this.player.x;
  }
  get playerY(): number {
    return this.player.y;
  }

  reset(seed: number): void {
    this.rng.reseed(seed);
    this.player.reset();
    this.hazards.clear();
    this.enemies.clear();
    this.particles.clear();
    this.hearts.clear();
    this.boss.active = false;
    this.hitStop = 0;
    this.shake = 0;
    this.heartTimer = this.rng.range(HEART.minInterval, HEART.maxInterval);
    this.outro = false;
    this.flash = 0;
    this.slashCd = 0;
    this.slashFx = 0;
  }

  setIntent(i: MoveIntent): void {
    this.intent = i;
  }

  /**
   * Wind the stage down for a level's conclusion: the player can no longer be
   * hit, every enemy switches to its departure behaviour, and lingering
   * hazards get a short remaining life so they fade away instead of freezing
   * mid-screen when the level ends.
   */
  beginOutro(): void {
    this.outro = true;
    for (let i = 0; i < this.enemies.count; i++) {
      const e = this.enemies.items[i];
      if (e.dying <= 0) e.life = Math.min(e.life, 0.01);
    }
    for (let i = 0; i < this.hazards.count; i++) {
      const h = this.hazards.items[i];
      h.life = Math.min(h.life, 1.4);
      h.burstOnExpire = 0; // a wind-down, not a final trap
    }
  }

  // --- SpawnContext --------------------------------------------------------
  bullet(
    x: number,
    y: number,
    vx: number,
    vy: number,
    radius: number,
    opts?: Partial<Hazard>,
  ): Hazard | null {
    const h = this.hazards.spawn();
    if (!h) return null;
    h.resetCircle(x, y, vx, vy, radius, opts);
    if (this.difficulty !== 1) {
      h.vx *= this.difficulty;
      h.vy *= this.difficulty;
    }
    // Anything fired during the wind-down dissolves with the rest of the stage.
    if (this.outro) {
      h.life = Math.min(h.life, 1.2);
      h.burstOnExpire = 0;
    }
    return h;
  }

  rect(
    x: number,
    y: number,
    hw: number,
    hh: number,
    angle: number,
    opts?: Partial<Hazard>,
  ): Hazard | null {
    const h = this.hazards.spawn();
    if (!h) return null;
    h.resetRect(x, y, hw, hh, angle, opts);
    // Pillars/beams carry their velocity in opts; scale the resulting hazard.
    if (this.difficulty !== 1) {
      h.vx *= this.difficulty;
      h.vy *= this.difficulty;
    }
    if (this.outro) h.life = Math.min(h.life, 1.2);
    return h;
  }

  enemy(type: EnemyType, x: number, y: number, opts?: Partial<Enemy>): Enemy | null {
    const e = this.enemies.spawn();
    if (!e) return null;
    e.reset(type, x, y, opts);
    // Meteors carry an entry velocity; harder levels launch them faster.
    if (this.difficulty !== 1) {
      e.vx *= this.difficulty;
      e.vy *= this.difficulty;
    }
    this.onEnemySpawn.emit(e);
    return e;
  }

  attack(kind: EnemyAttack): void {
    this.onEnemyAttack.emit(kind);
  }

  dragon(cmd: DragonCmd): void {
    switch (cmd) {
      case "enter":
        this.boss.enter(ARENA_W);
        break;
      case "phase2":
        this.boss.setPhase(2);
        break;
      case "phase3":
        this.boss.setPhase(3);
        break;
      case "frenzy":
        this.boss.setPhase(4);
        break;
      case "finale":
        this.boss.beginFinale();
        break;
      case "die":
        // Scripted finale detonation (same path as being felled by the sword).
        this.killBoss();
        break;
      case "leave":
        this.boss.leave();
        break;
    }
    this.onBoss.emit(cmd);
  }

  /** Detonate the dragon: death FX + the defeated signal. Idempotent. */
  killBoss(): void {
    const boss = this.boss;
    if (!boss.active || boss.dying > 0) return;
    boss.hp = 0;
    boss.dying = DRAGON.deathTime;
    this.burst(boss.x, boss.y, 110, COLOR.dragonFire, 600, 1.2);
    this.shake = Math.max(this.shake, 18);
    this.flash = 1;
    this.onBossDefeated.emit();
  }

  // --- Particles -----------------------------------------------------------
  burst(x: number, y: number, n: number, color: number, speed: number, life: number): void {
    const budget = this.particles.capacity - this.particles.count;
    const count = Math.min(n, budget, PARTICLE_BUDGET - this.particles.count);
    for (let i = 0; i < count; i++) {
      const p = this.particles.spawn();
      if (!p) break;
      const a = this.rng.next() * TAU;
      const s = speed * (0.3 + this.rng.next() * 0.7);
      p.reset(
        x,
        y,
        Math.cos(a) * s,
        Math.sin(a) * s,
        life * (0.6 + this.rng.next() * 0.4),
        2 + this.rng.next() * 3,
        color,
        0.86,
      );
    }
  }

  // --- Simulation step -----------------------------------------------------
  step(dt: number): void {
    this.updatePlayer(dt);
    this.updateEnemies(dt);
    this.updateDragon(dt);
    this.updateHazards(dt);
    this.updateHearts(dt);
    this.updateParticles(dt);
    this.shake = damp(this.shake, 0, 9, dt);
    this.flash = Math.max(0, this.flash - dt * 3);
    if (this.slashCd > 0) this.slashCd -= dt;
    if (this.slashFx > 0) this.slashFx -= dt;
  }

  // --- Sword ------------------------------------------------------------------
  /**
   * Swing the sword: a radial slash around the player that destroys nearby
   * bullets and damages enemies. Returns false while on cooldown.
   */
  trySlash(): boolean {
    if (this.slashCd > 0 || !this.player.alive) return false;
    this.slashCd = SWORD.cooldown;
    this.slashFx = SWORD.fxTime;
    const px = this.player.x;
    const py = this.player.y;
    const r = SWORD.radius;

    // Cut circle bullets (beams/walls are unslashable architecture).
    let cut = 0;
    const hz = this.hazards;
    for (let i = 0; i < hz.count; ) {
      const h = hz.items[i];
      if (h.kind === HZ_CIRCLE && h.warnT <= 0 && dist2(px, py, h.x, h.y) <= (r + h.radius) ** 2) {
        this.burst(h.x, h.y, 5, h.color, 260, 0.3);
        hz.freeAt(i);
        cut++;
        continue;
      }
      i++;
    }

    // Damage enemies in reach.
    let hits = 0;
    const en = this.enemies;
    for (let i = 0; i < en.count; i++) {
      const e = en.items[i];
      if (e.dying > 0 || e.fade < 0.5) continue;
      if (dist2(px, py, e.x, e.y) > (r + e.radius) ** 2) continue;
      hits++;
      e.hp -= 1;
      e.hitFlash = 0.18;
      // Knockback: shove the enemy away from the blade so the hit lands
      // physically as well as visually (the white hit-flash).
      const kd = Math.hypot(e.x - px, e.y - py) || 1;
      e.x += ((e.x - px) / kd) * 34;
      e.y += ((e.y - py) / kd) * 34;
      this.burst(e.x, e.y, 8, e.color, 300, 0.35);
      if (e.hp <= 0) {
        e.dying = ENEMY.deathTime;
        this.burst(e.x, e.y, 30, e.color, 420, 0.6);
        this.shake = Math.max(this.shake, 6);
        this.onEnemyDeath.emit(e);
      }
    }

    // Damage the dragon boss — one hit per swing, regardless of how many of
    // its segments fall within reach (the body is huge; it shouldn't melt).
    const boss = this.boss;
    if (boss.active && !boss.leaving && boss.dying <= 0) {
      let bossHit = dist2(px, py, boss.x, boss.y) <= (r + boss.segs[0].r) ** 2;
      if (!bossHit) {
        for (const s of boss.segs) {
          if (dist2(px, py, s.x, s.y) <= (r + s.r) ** 2) {
            bossHit = true;
            break;
          }
        }
      }
      if (bossHit) {
        hits++;
        boss.hp -= 1;
        boss.hitFlash = 0.18;
        this.burst(boss.x, boss.y, 10, COLOR.dragonFire, 340, 0.4);
        this.shake = Math.max(this.shake, 4);
        if (boss.hp <= 0) this.killBoss();
      }
    }

    this.onSlash.emit({ hits, cut });
    return true;
  }

  private updatePlayer(dt: number): void {
    const p = this.player;
    p.px = p.x;
    p.py = p.y;
    if (p.invuln > 0) p.invuln -= dt;

    const i = this.intent;
    if (i.pointerActive) {
      // Drag-to-move: ease toward the (offset) finger/cursor target.
      p.x = damp(p.x, i.targetX, PLAYER.followLerp, dt);
      p.y = damp(p.y, i.targetY, PLAYER.followLerp, dt);
    } else if (i.axisX !== 0 || i.axisY !== 0) {
      // Keyboard: normalized velocity for consistent diagonal speed.
      const inv = 1 / Math.hypot(i.axisX, i.axisY);
      p.x += i.axisX * inv * PLAYER.maxSpeed * dt;
      p.y += i.axisY * inv * PLAYER.maxSpeed * dt;
    }
    p.vx = (p.x - p.px) / dt;
    p.vy = (p.y - p.py) / dt;
    p.x = clamp(p.x, PLAYER.radius, ARENA_W - PLAYER.radius);
    p.y = clamp(p.y, PLAYER.radius, ARENA_H - PLAYER.radius);
  }

  private updateEnemies(dt: number): void {
    const pool = this.enemies;
    const px = this.player.x;
    const py = this.player.y;
    const canHit = this.player.alive && this.player.invuln <= 0 && !this.outro;
    const margin = 160;

    for (let idx = 0; idx < pool.count; ) {
      const e = pool.items[idx];
      e.px = e.x;
      e.py = e.y;
      if (e.fade < 1) e.fade = Math.min(1, e.fade + dt * 3);

      if (e.dying > 0) {
        // Death animation only; no AI, no collision.
        e.dying -= dt;
        if (e.dying <= 0) {
          pool.freeAt(idx);
          continue;
        }
        idx++;
        continue;
      }

      e.age += dt;
      e.life -= dt;
      // Out of stage time: switch to the departure behaviour (enemies only die
      // to the sword; they are never scripted to explode).
      if (e.life <= 0) e.beginLeave(this);
      e.update(dt, this);

      // Silently despawn once far off the field (Beast is exempt between charges).
      if (
        !e.noCull &&
        e.age > 2 &&
        (e.x < -margin || e.x > ARENA_W + margin || e.y < -margin || e.y > ARENA_H + margin)
      ) {
        pool.freeAt(idx);
        continue;
      }

      // Body collision & graze (after a short spawn grace).
      if (canHit && e.age > ENEMY.spawnGrace) {
        const bodyR = e.radius * 0.85;
        if (circlesOverlap(px, py, PLAYER.hitRadius, e.x, e.y, bodyR)) {
          this.damagePlayer(px, py);
        } else if (!e.grazed && circlesOverlap(px, py, PLAYER.grazeRadius, e.x, e.y, bodyR)) {
          e.grazed = true;
          this.shake = Math.max(this.shake, JUICE.grazeShake);
          this.onGraze.emit({ x: px, y: py });
          this.burst(px, py, 4, e.color, 220, 0.3);
        }
      }
      idx++;
    }
  }

  private updateDragon(dt: number): void {
    const d = this.boss;
    if (!d.active) return;

    if (d.dying > 0) {
      // Death sequence: frozen in place, no attacks, no collision — just the
      // render-side death FX until the countdown ends.
      d.dying -= dt;
      if (d.dying <= 0) d.active = false;
      return;
    }

    d.update(dt, this);
    if (d.justRoared) {
      d.justRoared = false;
      this.shake = Math.max(this.shake, 10);
      this.onRoar.emit();
    }

    // Every segment of the dragon is lethal; the head grazes like a hazard.
    if (!this.player.alive || this.player.invuln > 0 || this.outro || d.leaving) return;
    const px = this.player.x;
    const py = this.player.y;
    const headR = this.boss.segs[0].r * 1.1;
    if (circlesOverlap(px, py, PLAYER.hitRadius, d.x, d.y, headR)) {
      this.damagePlayer(px, py);
      return;
    }
    for (const s of d.segs) {
      if (circlesOverlap(px, py, PLAYER.hitRadius, s.x, s.y, s.r * 0.8)) {
        this.damagePlayer(px, py);
        return;
      }
    }
  }

  private updateHazards(dt: number): void {
    const pool = this.hazards;
    const px = this.player.x;
    const py = this.player.y;
    const canHit = this.player.alive && this.player.invuln <= 0 && !this.outro;
    const margin = 80;

    for (let idx = 0; idx < pool.count; ) {
      const h = pool.items[idx];
      h.px = h.x;
      h.py = h.y;

      // Telegraph countdown.
      if (h.warnT > 0) {
        h.warnT -= dt;
        if (h.warnT < 0) h.warnT = 0;
      }
      // Spawn/active fade-in. During the level outro, expiring hazards also
      // dissolve instead of popping (they're harmless then, so the fade can't
      // hide live danger).
      if (h.fade < 1) h.fade = Math.min(1, h.fade + dt * 6);
      if (this.outro && h.active && h.life < 0.45) {
        h.fade = Math.min(h.fade, Math.max(0, h.life / 0.45));
      }

      // Motion (telegraphed beams usually have zero velocity).
      if (h.ay !== 0) h.vy += h.ay * dt; // ballistic bombs
      h.x += h.vx * dt;
      h.y += h.vy * dt;
      if (h.spin !== 0) h.angle += h.spin * dt;

      if (h.active) h.life -= dt;

      // Cull: expired, or fully off-arena while moving.
      let dead = h.life <= 0;
      if (dead && h.burstOnExpire > 0) {
        // Bomb detonation: a ring of shrapnel from the point of expiry.
        const n = h.burstOnExpire;
        const sp = h.burstSpeed;
        const bx = h.x;
        const by = h.y;
        this.burst(bx, by, 22, COLOR.accent, 420, 0.5);
        this.shake = Math.max(this.shake, 8);
        this.onExplosion.emit({ x: bx, y: by });
        for (let i = 0; i < n; i++) {
          const a = (i / n) * TAU;
          this.bullet(bx, by, Math.cos(a) * sp, Math.sin(a) * sp, 8, { color: COLOR.bullet });
        }
      }
      if (!dead && h.cullOffscreen && (h.vx !== 0 || h.vy !== 0)) {
        const r = h.kind === HZ_CIRCLE ? h.radius : Math.max(h.hw, h.hh);
        if (
          h.x < -margin - r ||
          h.x > ARENA_W + margin + r ||
          h.y < -margin - r ||
          h.y > ARENA_H + margin + r
        ) {
          dead = true;
        }
      }
      if (dead) {
        pool.freeAt(idx);
        continue;
      }

      // Collision & graze (only once telegraph has elapsed).
      if (h.active && canHit) {
        if (hazardHitsCircle(h, px, py, PLAYER.hitRadius)) {
          this.damagePlayer(px, py);
          // Player is now invulnerable; stop checking further hazards for hits.
        } else if (!h.grazed && hazardHitsCircle(h, px, py, PLAYER.grazeRadius)) {
          h.grazed = true;
          this.shake = Math.max(this.shake, JUICE.grazeShake);
          this.onGraze.emit({ x: px, y: py });
          this.burst(px, py, 4, h.color, 220, 0.3);
        }
      }
      idx++;
    }
  }

  private damagePlayer(x: number, y: number): void {
    const p = this.player;
    p.lives -= 1;
    p.invuln = PLAYER.invulnTime;
    this.hitStop = JUICE.hitStopTime;
    this.shake = JUICE.hitShake;
    this.flash = 1;
    const fatal = p.lives <= 0;
    if (fatal) p.alive = false;
    this.burst(x, y, fatal ? 64 : 28, COLOR.player, 520, 0.8);
    this.onHit.emit({ x, y, fatal });
  }

  private updateHearts(dt: number): void {
    if (this.player.alive) {
      this.heartTimer -= dt;
      if (this.heartTimer <= 0) {
        this.heartTimer = this.rng.range(HEART.minInterval, HEART.maxInterval);
        // Only worth spawning if the player actually has a life to gain back.
        if (this.player.lives < PLAYER.startLives) this.spawnHeart();
      }
    }

    const pool = this.hearts;
    const margin = 60;
    for (let idx = 0; idx < pool.count; ) {
      const h = pool.items[idx];
      h.px = h.x;
      h.py = h.y;
      h.x += h.vx * dt;
      h.y += h.vy * dt;
      h.life -= dt;

      let dead = h.life <= 0;
      if (
        !dead &&
        (h.x < -margin || h.x > ARENA_W + margin || h.y < -margin || h.y > ARENA_H + margin)
      ) {
        dead = true;
      }

      if (
        !dead &&
        this.player.alive &&
        this.player.lives < PLAYER.startLives &&
        circlesOverlap(h.x, h.y, HEART.pickupRadius, this.player.x, this.player.y, PLAYER.radius)
      ) {
        this.player.lives = Math.min(this.player.lives + 1, PLAYER.startLives);
        this.burst(h.x, h.y, 24, COLOR.heart, 300, 0.6);
        this.onHeal.emit({ x: h.x, y: h.y });
        dead = true;
      }

      if (dead) {
        pool.freeAt(idx);
        continue;
      }
      idx++;
    }
  }

  /** Float a heart in from a random edge, drifting gently across the arena. */
  private spawnHeart(): void {
    const h = this.hearts.spawn();
    if (!h) return;

    const fromLeft = this.rng.next() < 0.5;
    const x = fromLeft ? -HEART.radius : ARENA_W + HEART.radius;
    const y = this.rng.range(ARENA_H * 0.15, ARENA_H * 0.65);
    const dirX = fromLeft ? 1 : -1;
    const angle = this.rng.range(-0.3, 0.3);
    const vx = Math.cos(angle) * HEART.speed * dirX;
    const vy = Math.sin(angle) * HEART.speed;
    h.reset(x, y, vx, vy, HEART.radius, HEART.life, this.rng.next() * TAU);
  }

  private updateParticles(dt: number): void {
    const pool = this.particles;
    for (let idx = 0; idx < pool.count; ) {
      const p = pool.items[idx];
      p.px = p.x;
      p.py = p.y;
      p.life -= dt;
      if (p.life <= 0) {
        pool.freeAt(idx);
        continue;
      }
      const d = Math.pow(p.drag, dt * 60);
      p.vx *= d;
      p.vy *= d;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      idx++;
    }
  }
}
